import React from "react";
import { STATE_KEYS } from "utils/constants/state";
import Toggle from "react-toggle";
import classnames from "classnames";
import { getToggleProps } from "utils/form";
import Button from "components/Form/fields/Button";
import { BUTTON_CLASS_CONTEXT } from "utils/constants/ui";
import { useFormikContext } from "formik";
import get from "lodash/get";
import startCase from "lodash/startCase";
import cloneDeep from "lodash/cloneDeep";
import {
  CLUSIVITY_TYPES,
  FEATURE_ORDER_KEY_MAP,
  ORDER_BY_KEYS,
  DEFAULT_FEATURE_ORDER
} from "components/FormerEditor/common/constants";
import { FEATURE_LIST_INTENT } from "utils/constants/feature";
import MenuNavList from "components/FormerEditor/MenuNavList";
import { COLORS } from "utils/styles";
import {
  resetProductFeatureLayouts,
  anyProductFeatureLayout
} from "utils/feature";
import HoverIconButton from "components/HoverIconButton";

const ASC_DESC_OPTIONS = ["asc", "desc"];

const OrderSettings = ({ name }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const order = cloneDeep(
    get(values, STATE_KEYS.MANIFEST.FEATURES_ORDER, DEFAULT_FEATURE_ORDER)
  );
  const clusivity = get(values, STATE_KEYS.MANIFEST.FEATURES_CLUSIVITY);

  const productsPath = `${name}.data.products`;
  const products = get(values, productsPath, []);
  const featuresConfig = get(values, STATE_KEYS.MANIFEST.FEATURES);

  const clearProductFeatureLayout = (id) => {
    return () => {
      const productFeaturePath = `${STATE_KEYS.MANIFEST.FEATURES}.${id}.layout`;

      setFieldValue(productFeaturePath, []);
      setFieldTouched(productFeaturePath, true);
    };
  };

  const clearAllProductFeatureLayout = () => {
    const updatedFeatureConfig = resetProductFeatureLayouts(featuresConfig);
    setFieldValue(STATE_KEYS.MANIFEST.FEATURES, updatedFeatureConfig);
    setFieldTouched(STATE_KEYS.MANIFEST.FEATURES, true);
  };

  const MENU_ITEMS_CONFIG = [
    ...products.map((product) => {
      const productFeaturePath = `${STATE_KEYS.MANIFEST.FEATURES}.${product.uid}.layout`;
      const productFeatureLayout = get(values, productFeaturePath, []);
      const disabled = !productFeatureLayout.length;
      return {
        label: product.title,
        action: {
          type: "refresh",
          tooltip: !disabled ? "Remove custom order" : "",
          disabled,
          color: COLORS.blue,
          onClick: clearProductFeatureLayout(product.uid)
        },
        focus: [
          {
            key: STATE_KEYS.EDITOR.FEATURE_INTENT,
            value: FEATURE_LIST_INTENT.PRODUCT_FEATURE_ORDER
          },
          {
            key: STATE_KEYS.EDITOR.FEATURE_ACTIVE_PRODUCT,
            value: product.uid
          }
        ]
      };
    })
  ];

  const disableClearAll = !anyProductFeatureLayout(featuresConfig);

  /**
   * Deactivate the order by active toggle when the clusivity type is not inclusive
   * All non inclusive clusivity types only have active features so the active toggle becomes redundant
   */
  if (clusivity !== CLUSIVITY_TYPES.INCLUSIVE) {
    const activeIndex = order.findIndex(
      ({ key }) => key === ORDER_BY_KEYS.ACTIVE
    );
    if (activeIndex > -1) {
      order[activeIndex].disabled = true;
    }
  }

  return (
    <div className="flex flex-column justify-between items-center">
      <div className="ph3 w-100">
        <div className="f4 fw6 flex w-100 pb3">Ordering</div>
        <div className="flex flex-column w-100 pt3">
          {order.map((orderConfig, orderConfigIx) => {
            const configPath = `${STATE_KEYS.MANIFEST.FEATURES_ORDER}[${orderConfigIx}]`;

            const label =
              FEATURE_ORDER_KEY_MAP[orderConfig.key] ||
              startCase(orderConfig.key);
            return (
              <div
                key={orderConfigIx}
                className="w-100 flex flex-row items-center justify-between pv3 bt hairline-1"
              >
                <div
                  className={classnames("f5", {
                    "o-50": orderConfig.disabled
                  })}
                >
                  {label}
                </div>
                <div className="flex flex-row jsutify-center items-center">
                  {orderConfig.toggle && (
                    <div className="flex flex-row">
                      {ASC_DESC_OPTIONS.map((orderOpt, orderOptIx) => (
                        <Button
                          key={orderOptIx}
                          theme="slim"
                          disabled={orderConfig.disabled}
                          onClick={() => {
                            const updatedConfig = {
                              ...orderConfig,
                              value: orderOpt
                            };
                            setFieldValue(configPath, updatedConfig);
                          }}
                          classes={{
                            button: classnames(
                              "fw6 br2 ph3 pv2 border-box input-reset ba b--black bg-transparent pointer f6 dib link items-center flex flex-shrink-0",
                              {
                                "br2 br--left": !orderOptIx,
                                "br2 br--right":
                                  orderOptIx === ASC_DESC_OPTIONS.length - 1
                              }
                            ),
                            contextClass:
                              orderOpt === orderConfig.value
                                ? BUTTON_CLASS_CONTEXT.PRIMARY
                                : BUTTON_CLASS_CONTEXT.SECONDARY
                          }}
                          copy={startCase(orderOpt)}
                        />
                      ))}
                    </div>
                  )}
                  <div
                    className={classnames("flex items-center", {
                      "ml3 pl3 h-100 bl b--black-20 hairline-1":
                        orderConfig.toggle
                    })}
                  >
                    <Toggle
                      {...getToggleProps({
                        active: orderConfig.active,
                        disabled: orderConfig.disabled,
                        setFieldValue: (_, active) => {
                          const updatedConfig = {
                            ...orderConfig,
                            active
                          };

                          setFieldValue(configPath, updatedConfig);
                        }
                      })}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="pt3 w-100">
        <div className="f6 ttu fw5 flex w-100 pt2 pb3 ph3">Custom</div>
        <div className="w-100">
          <div className="lh-title bg-content--secondary bb bt hairline-1 bh--inset flex flex-row pa3">
            <div className="f7">
              Click on a product to set a custom order for its top features.
            </div>
            <HoverIconButton
              onClick={clearAllProductFeatureLayout}
              disabled={disableClearAll}
              type="refresh"
              tooltip={!disableClearAll ? "Remove all custom ordering" : ""}
              color={COLORS.blue}
            />
          </div>
          <MenuNavList config={MENU_ITEMS_CONFIG} />
        </div>
      </div>
    </div>
  );
};

export default OrderSettings;
